// src/components/dev/GuideDevPanel.js
// Only rendered in development mode. Never ships to production.
//
// Toggle open/closed with: Shift + G
//
// Reads and writes real Firestore data, so you're testing the actual
// guide system — not a simulation.
//
// Sections:
//   Guides      — Shows each guide's eligibility and hard-dismiss state.
//                 Green ● = currently visible (all conditions met).
//                 Gray ○ = not visible (dismissed, conditions unmet, or completedBy done).
//
//   Action Counts — Shows every tracked action count. Increment simulates the
//                 action firing (including auto-dismissals on first occurrence).
//                 Reset zeroes the count and un-dismisses any guide whose
//                 auto-dismissal was triggered by that action.

import React, { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot, setDoc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { useAuth } from '../../firebase/AuthContext';
import { GUIDE_CONFIG, ALL_TRACKED_ACTIONS } from '../../config/guideConfig';
import { incrementActionCount, resetActionCount } from '../../hooks/useGuide';

const GuideDevPanel = () => {
  const { currentUser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [prefs, setPrefs] = useState({ guides: {}, actionCounts: {} });

  // Toggle with Shift+G
  useEffect(() => {
    const handleKey = (e) => {
      if (e.shiftKey && e.key === 'G') setIsOpen((v) => !v);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  useEffect(() => {
    if (!isOpen || !currentUser) return;

    const unsubscribe = onSnapshot(
      doc(db, 'userPreferences', currentUser.uid),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setPrefs({
            guides: data.guides || {},
            actionCounts: data.actionCounts || {},
          });
        } else {
          setPrefs({ guides: {}, actionCounts: {} });
        }
      }
    );

    return unsubscribe;
  }, [isOpen, currentUser]);

  // ─── Guide controls ────────────────────────────────────────────────────────

  const setGuide = async (guideId, dismissed) => {
    const ref = doc(db, 'userPreferences', currentUser.uid);
    if (dismissed) {
      await setDoc(ref, { guides: { [guideId]: true } }, { merge: true });
    } else {
      await updateDoc(ref, { [`guides.${guideId}`]: deleteField() });
    }
  };

  // ─── Action count controls ─────────────────────────────────────────────────

  const handleIncrement = async (actionId) => {
    await incrementActionCount(currentUser.uid, actionId);
  };

  const handleReset = async (actionId) => {
    try {
      await resetActionCount(currentUser.uid, actionId);
    } catch {
      // resetActionCount uses updateDoc, which fails if the doc doesn't exist.
      // In that case there's nothing to reset — ignore silently.
    }
  };

  // ─── Per-guide test setup ─────────────────────────────────────────────────
  // Resets guide + completedBy counts, then ensures showWhen minimums are met.
  // Leaves unrelated action counts (e.g. other guides' triggers) untouched.

  const testGuide = async (guideId) => {
    const config = GUIDE_CONFIG[guideId];
    const ref = doc(db, 'userPreferences', currentUser.uid);
    const completedByList = Array.isArray(config.completedBy)
      ? config.completedBy
      : config.completedBy ? [config.completedBy] : [];

    // Step 1: clear guide dismiss + zero out completedBy counts
    const deletes = { [`guides.${guideId}`]: deleteField() };
    completedByList.forEach(id => { deletes[`actionCounts.${id}`] = deleteField(); });
    try { await updateDoc(ref, deletes); } catch { /* doc doesn't exist — nothing to delete */ }

    // Step 2: bump any showWhen action that hasn't reached its minimum yet
    const bumps = {};
    config.showWhen.forEach(({ actionId, minCount }) => {
      if ((prefs.actionCounts?.[actionId] ?? 0) < minCount) bumps[actionId] = minCount;
    });
    if (Object.keys(bumps).length > 0) {
      await setDoc(ref, { actionCounts: bumps }, { merge: true });
    }

  };

  // ─── Reset all ────────────────────────────────────────────────────────────

  const resetAll = async () => {
    const ref = doc(db, 'userPreferences', currentUser.uid);
    await setDoc(ref, { guides: {}, actionCounts: {} });
  };

  // ─── Guide eligibility helpers ─────────────────────────────────────────────

  const isGuideEligible = (guideId) => {
    const config = GUIDE_CONFIG[guideId];
    const isDismissed = prefs.guides?.[guideId] === true;
    if (isDismissed) return false;

    const showWhenMet = config.showWhen.every(
      ({ actionId, minCount }) => (prefs.actionCounts?.[actionId] ?? 0) >= minCount
    );
    if (!showWhenMet) return false;

    const completedByList = Array.isArray(config.completedBy) ? config.completedBy : config.completedBy ? [config.completedBy] : [];
    const completedByCount = completedByList.reduce((sum, id) => sum + (prefs.actionCounts?.[id] ?? 0), 0);
    if (completedByCount > 0) return false;

    return true; // Note: localCondition is not reflected here — check the actual component
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        style={{
          position: 'fixed',
          bottom: '12px',
          left: '12px',
          zIndex: 9999,
          fontSize: '10px',
          padding: '4px 8px',
          background: '#303840',
          color: '#8F99A3',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          opacity: 0.6,
          fontFamily: 'monospace',
        }}
        title="Open Guide Dev Panel (Shift+G)"
      >
        GUIDES
      </button>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '12px',
        left: '12px',
        zIndex: 9999,
        width: '300px',
        maxHeight: '80vh',
        overflowY: 'auto',
        background: '#303840',
        color: '#F2F5F7',
        borderRadius: '8px',
        padding: '12px',
        fontFamily: 'monospace',
        fontSize: '11px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <span style={{ fontWeight: 'bold', color: '#F2F5F7' }}>Guide Dev Panel</span>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={resetAll}
            style={{ fontSize: '10px', background: '#F9574E', color: 'white', border: 'none', borderRadius: '3px', padding: '2px 6px', cursor: 'pointer' }}
          >
            Reset All
          </button>
          <button
            onClick={() => setIsOpen(false)}
            style={{ fontSize: '10px', background: '#525C65', color: 'white', border: 'none', borderRadius: '3px', padding: '2px 6px', cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* ── Guides ─────────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ color: '#8F99A3', marginBottom: '6px', textTransform: 'uppercase', fontSize: '10px', letterSpacing: '0.05em' }}>
          Guides
        </div>
        <div style={{ color: '#525C65', fontSize: '9px', marginBottom: '6px' }}>
          ● = eligible (Firestore only — localCondition not reflected)
        </div>

        {Object.keys(GUIDE_CONFIG).map((guideId) => {
          const config = GUIDE_CONFIG[guideId];
          const isDismissed = prefs.guides?.[guideId] === true;
          const eligible = isGuideEligible(guideId);

          const showWhenLabels = config.showWhen.map(
            ({ actionId, minCount }) =>
              `${actionId} ≥ ${minCount} (now: ${prefs.actionCounts?.[actionId] ?? 0})`
          );

          const completedByList = Array.isArray(config.completedBy) ? config.completedBy : config.completedBy ? [config.completedBy] : [];
          const completedByCount = completedByList.reduce((sum, id) => sum + (prefs.actionCounts?.[id] ?? 0), 0);

          return (
            <div
              key={guideId}
              style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #3D4850' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ color: eligible ? '#10B981' : '#707A84' }}>
                    {eligible ? '● ' : '○ '}
                  </span>
                  <span style={{ color: '#E1E5EA' }}>{guideId}</span>

                  {showWhenLabels.length > 0 && (
                    <div style={{ color: '#707A84', fontSize: '9px', paddingLeft: '14px', marginTop: '2px' }}>
                      show when: {showWhenLabels.join(', ')}
                    </div>
                  )}

                  {completedByList.length > 0 && (
                    <div style={{ color: completedByCount > 0 ? '#F9574E' : '#707A84', fontSize: '9px', paddingLeft: '14px' }}>
                      completedBy: {completedByList.map(id => `${id}(${prefs.actionCounts?.[id] ?? 0})`).join(' + ')} = {completedByCount}
                      {completedByCount > 0 && ' ← suppressed'}
                    </div>
                  )}

                  {isDismissed && (
                    <div style={{ color: '#F9574E', fontSize: '9px', paddingLeft: '14px' }}>
                      hard-dismissed in Firestore
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '4px', flexShrink: 0, marginLeft: '8px' }}>
                  <button
                    onClick={() => testGuide(guideId)}
                    title="Reset to testable state (clears dismiss + completedBy, ensures showWhen minimums). Reload page if guide still doesn't appear."
                    style={{
                      fontSize: '10px',
                      background: '#1D4ED8',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      padding: '2px 6px',
                      cursor: 'pointer',
                    }}
                  >
                    Test
                  </button>
                  <button
                    onClick={() => setGuide(guideId, !isDismissed)}
                    style={{
                      fontSize: '10px',
                      background: isDismissed ? '#525C65' : '#0C141D',
                      color: 'white',
                      border: '1px solid #525C65',
                      borderRadius: '3px',
                      padding: '2px 6px',
                      cursor: 'pointer',
                    }}
                  >
                    {isDismissed ? 'Reset' : 'Dismiss'}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Action Counts ───────────────────────────────────────────────────── */}
      <div>
        <div style={{ color: '#8F99A3', marginBottom: '6px', textTransform: 'uppercase', fontSize: '10px', letterSpacing: '0.05em' }}>
          Action Counts
        </div>
        <div style={{ color: '#525C65', fontSize: '9px', marginBottom: '6px' }}>
          Increment = simulate action (fires auto-dismiss on first hit)
        </div>

        {ALL_TRACKED_ACTIONS.map((actionId) => {
          const count = prefs.actionCounts?.[actionId] ?? 0;

          // Find any guide completed by this action (for labeling)
          const linkedGuide = Object.entries(GUIDE_CONFIG).find(([, config]) => {
            const list = Array.isArray(config.completedBy) ? config.completedBy : config.completedBy ? [config.completedBy] : [];
            return list.includes(actionId);
          })?.[0];

          return (
            <div
              key={actionId}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ color: count > 0 ? '#E1E5EA' : '#707A84' }}>
                  {actionId}
                </span>
                <span style={{
                  color: count > 0 ? '#10B981' : '#525C65',
                  marginLeft: '4px',
                  fontWeight: count > 0 ? 'bold' : 'normal',
                }}>
                  ({count})
                </span>
                {linkedGuide && (
                  <span style={{ color: '#525C65', fontSize: '9px', marginLeft: '4px' }}>
                    → {linkedGuide}
                  </span>
                )}
              </div>

              <div style={{ display: 'flex', gap: '4px', flexShrink: 0, marginLeft: '6px' }}>
                <button
                  onClick={() => handleIncrement(actionId)}
                  style={{
                    fontSize: '10px',
                    background: '#10B981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '3px',
                    padding: '2px 5px',
                    cursor: 'pointer',
                  }}
                  title={`Increment ${actionId}`}
                >
                  +1
                </button>
                <button
                  onClick={() => handleReset(actionId)}
                  style={{
                    fontSize: '10px',
                    background: count > 0 ? '#525C65' : '#303840',
                    color: count > 0 ? 'white' : '#525C65',
                    border: '1px solid #525C65',
                    borderRadius: '3px',
                    padding: '2px 5px',
                    cursor: count > 0 ? 'pointer' : 'default',
                  }}
                  title={`Reset ${actionId} to 0`}
                  disabled={count === 0}
                >
                  ↺
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GuideDevPanel;